import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { DELETE_INVESTMENT_VEHICLE_MUTATION } from '../../queries';
import { InvestmentVehicleDto } from '../types';

interface DeleteVehicleProps {
  isOpen: boolean;
  currentVehicle: InvestmentVehicleDto | null;
  investmentVehiclesRefetch: () => void;
  onClose: () => void;
}

const DeleteVehicle = ({ isOpen, currentVehicle, investmentVehiclesRefetch, onClose }: DeleteVehicleProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [deleteInvestmentVehicle, { loading: deleteInvestmentVehicleLoading }] = useMutation(DELETE_INVESTMENT_VEHICLE_MUTATION);

  const saveValue = async () => {
    setErrorMessage(null);

    if (!currentVehicle?.id) {
      setErrorMessage(defaultErrorMessage);
      return;
    }

    deleteInvestmentVehicle({
      variables: {
        id: currentVehicle.id
      },
      onCompleted: () => {
        investmentVehiclesRefetch();
        onClose();
      },
      onError: () => {
        setErrorMessage(defaultErrorMessage);
      }
    });
  };

  if (currentVehicle?.hasAllocations || currentVehicle?.hasCommitments) {
    return (
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={onClose}
        showCancelButton={false}
        confirmButton={{
          name: 'OK',
          onClick: onClose
        }}
      >
        <ModalBody>
          <ModalTitle>Cannot Delete Vehicle</ModalTitle>
          This vehicle is associated with active commitments and cannot be deleted.
        </ModalBody>
      </ModalWrappedContent>
    );
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!deleteInvestmentVehicleLoading}
      isUseOutsideClick={!deleteInvestmentVehicleLoading}
      showCancelButton={!deleteInvestmentVehicleLoading}
      confirmButton={{
        name: 'Yes, Delete',
        onClick: saveValue,
        disabled: deleteInvestmentVehicleLoading
      }}
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        {deleteInvestmentVehicleLoading ? (
          <ModalLoader />
        ) : (
          <>
            <ModalTitle>Please confirm you want to delete this vehicle.</ModalTitle>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteVehicle;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
  text-align: center;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${({ theme }) => theme.font.strong};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
